import React, { useState, useEffect } from 'react';
import { vehicleAPI, otpAPI } from '../services/api';
import { Car, LogIn as CheckIn, LogOut as CheckOut, Search, X, Filter, Phone, CheckCircle, Upload, Camera, UserCheck } from 'lucide-react';

const Vehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [visitorImagePreview, setVisitorImagePreview] = useState(null);
  const [vehicleImagePreview, setVehicleImagePreview] = useState(null);
  const [isWalkIn, setIsWalkIn] = useState(false);
  
  const [formData, setFormData] = useState({
    vehicleNumber: '',
    vehicleType: 'car',
    visitorName: '',
    visitorPhone: '',
    flatNumber: '',
    notes: '',
    visitorImage: null,
    vehicleImage: null,
  });

  useEffect(() => {
    fetchVehicles();
  }, [statusFilter]);

  const fetchVehicles = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      
      const response = await vehicleAPI.getAllVehicles(params);
      setVehicles(response.data.vehicles);
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async () => {
    if (!formData.visitorPhone) {
      alert('Please enter visitor phone number first');
      return;
    }

    try {
      const response = await otpAPI.sendOTP({
        phone: formData.visitorPhone,
        purpose: 'vehicle-checkin'
      });
      setOtpSent(true);
      setShowOTPModal(true);
      alert(response.data.message + (response.data.otp ? ` (OTP: ${response.data.otp})` : ''));
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to send OTP');
    }
  };

  const handleVerifyOTP = async () => {
    try {
      await otpAPI.verifyOTP({
        phone: formData.visitorPhone,
        otp: otp,
        purpose: 'vehicle-checkin'
      });
      setOtpVerified(true);
      setShowOTPModal(false);
      alert('Phone number verified successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Invalid OTP');
    }
  };

  const handleImageChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      if (type === 'visitor') {
        setFormData({ ...formData, visitorImage: file });
        setVisitorImagePreview(URL.createObjectURL(file));
      } else {
        setFormData({ ...formData, vehicleImage: file });
        setVehicleImagePreview(URL.createObjectURL(file));
      }
    }
  };

  const handleCheckIn = async (e) => {
    e.preventDefault();

    // Validate OTP verification
    if (!otpVerified) {
      alert('Please verify visitor phone number with OTP');
      return;
    }

    // Validate visitor image
    if (!formData.visitorImage) {
      alert('Visitor image is required');
      return;
    }

    // Validate vehicle fields only if not walk-in
    if (!isWalkIn) {
      if (!formData.vehicleNumber || !formData.vehicleType || !formData.vehicleImage) {
        alert('Please provide vehicle details (number, type, and image) for non-walk-in visitors');
        return;
      }
    }

    try {
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('isWalkIn', isWalkIn);
      submitData.append('visitorName', formData.visitorName);
      submitData.append('visitorPhone', formData.visitorPhone);
      submitData.append('flatNumber', formData.flatNumber);
      submitData.append('notes', formData.notes || '');
      submitData.append('visitorImage', formData.visitorImage);
      submitData.append('otpVerified', 'true');
      
      // Add vehicle details only if not walk-in
      if (!isWalkIn) {
        submitData.append('vehicleNumber', formData.vehicleNumber);
        submitData.append('vehicleType', formData.vehicleType);
        if (formData.vehicleImage) {
          submitData.append('vehicleImage', formData.vehicleImage);
        }
      }

      await vehicleAPI.checkIn(submitData);
      setShowCheckInModal(false);
      resetForm();
      fetchVehicles();
      alert(isWalkIn ? 'Walk-in visitor checked in successfully!' : 'Vehicle checked in successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Check-in failed');
    }
  };

  const handleCheckOut = async (id) => {
    if (window.confirm('Are you sure you want to check out this vehicle?')) {
      try {
        await vehicleAPI.checkOut(id);
        fetchVehicles();
        alert('Vehicle checked out successfully!');
      } catch (error) {
        alert(error.response?.data?.message || 'Check-out failed');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      vehicleNumber: '',
      vehicleType: 'car',
      visitorName: '',
      visitorPhone: '',
      flatNumber: '',
      notes: '',
      visitorImage: null,
      vehicleImage: null,
    });
    setOtpSent(false);
    setOtpVerified(false);
    setOtp('');
    setVisitorImagePreview(null);
    setVehicleImagePreview(null);
    setIsWalkIn(false);
  };

  const handleSearch = () => {
    fetchVehicles();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Vehicle Management</h1>
          <p className="mt-2 text-sm text-gray-700">
            Check-in and check-out vehicles with visitor verification
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={() => {
              resetForm();
              setShowCheckInModal(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <CheckIn className="h-5 w-5 mr-2" />
            Check-in Vehicle
          </button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="sm:col-span-2 relative">
          <input
            type="text"
            placeholder="Search by vehicle number, visitor name, or flat..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
        </div>

        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
          >
            <option value="">All Status</option>
            <option value="checked-in">Checked-in</option>
            <option value="checked-out">Checked-out</option>
          </select>
          <button
            onClick={handleSearch}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
          >
            <Filter className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Vehicles Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Vehicle
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Visitor
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Flat
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Check-in Time
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {vehicles.map((vehicle) => (
              <tr key={vehicle._id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {vehicle.isWalkIn ? (
                      <UserCheck className="h-5 w-5 text-blue-500 mr-2" />
                    ) : (
                      <Car className="h-5 w-5 text-gray-400 mr-2" />
                    )}
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {vehicle.isWalkIn ? 'Walk-in Visitor' : vehicle.vehicleNumber}
                      </div>
                      <div className="text-sm text-gray-500 capitalize">
                        {vehicle.isWalkIn ? 'No Vehicle' : vehicle.vehicleType}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{vehicle.visitorName}</div>
                  <div className="text-sm text-gray-500">{vehicle.visitorPhone}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {vehicle.flatNumber}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(vehicle.checkInTime).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      vehicle.status === 'checked-in'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {vehicle.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {vehicle.status === 'checked-in' && (
                    <button
                      onClick={() => handleCheckOut(vehicle._id)}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                    >
                      <CheckOut className="h-4 w-4 mr-1" />
                      Check Out
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {vehicles.length === 0 && (
        <div className="text-center py-12">
          <Car className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No vehicles</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by checking in a vehicle.</p>
        </div>
      )}

      {/* Check-in Modal */}
      {showCheckInModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowCheckInModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <form onSubmit={handleCheckIn}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      {isWalkIn ? 'Check-in Walk-in Visitor' : 'Check-in Vehicle'}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowCheckInModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  {/* Walk-in Toggle */}
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isWalkIn}
                        onChange={(e) => setIsWalkIn(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 flex items-center text-sm font-medium text-gray-900">
                        <UserCheck className="h-4 w-4 mr-1 text-blue-600" />
                        Walk-in Visitor (No Vehicle)
                      </span>
                    </label>
                    <p className="mt-1 ml-6 text-xs text-gray-600">
                      Check this if the visitor is coming on foot without any vehicle
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {!isWalkIn && (
                      <>
                        <div className="sm:col-span-2">
                          <label className="block text-sm font-medium text-gray-700">Vehicle Number *</label>
                          <input
                            type="text"
                            required={!isWalkIn}
                            value={formData.vehicleNumber}
                            onChange={(e) => setFormData({ ...formData, vehicleNumber: e.target.value.toUpperCase() })}
                            placeholder="MH12AB1234"
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm uppercase"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">Vehicle Type *</label>
                          <select
                            value={formData.vehicleType}
                            onChange={(e) => setFormData({ ...formData, vehicleType: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          >
                            <option value="car">Car</option>
                            <option value="bike">Bike</option>
                            <option value="truck">Truck</option>
                            <option value="van">Van</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                      </>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Flat Number *</label>
                      <input
                        type="text"
                        required
                        value={formData.flatNumber}
                        onChange={(e) => setFormData({ ...formData, flatNumber: e.target.value })}
                        placeholder="A-101"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Visitor Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.visitorName}
                        onChange={(e) => setFormData({ ...formData, visitorName: e.target.value })}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Visitor Phone * {otpVerified && <CheckCircle className="inline h-4 w-4 text-green-500" />}
                      </label>
                      <div className="mt-1 flex rounded-md shadow-sm">
                        <input
                          type="tel"
                          required
                          value={formData.visitorPhone}
                          onChange={(e) => setFormData({ ...formData, visitorPhone: e.target.value })}
                          disabled={otpVerified}
                          className="flex-1 block w-full border border-gray-300 rounded-l-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        />
                        {!otpVerified && (
                          <button
                            type="button"
                            onClick={handleSendOTP}
                            className="inline-flex items-center px-4 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-700 text-sm hover:bg-gray-100"
                          >
                            <Phone className="h-4 w-4 mr-1" />
                            {otpSent ? 'Resend' : 'Verify'}
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Visitor Image * (Required)</label>
                      <div className="mt-1 flex items-center space-x-4">
                        <label className="flex-1 cursor-pointer">
                          <div className="flex items-center justify-center px-6 py-4 border-2 border-gray-300 border-dashed rounded-md hover:border-primary-500">
                            <div className="text-center">
                              <Camera className="mx-auto h-8 w-8 text-gray-400" />
                              <p className="mt-1 text-sm text-gray-600">
                                {formData.visitorImage ? formData.visitorImage.name : 'Click to upload visitor photo'}
                              </p>
                            </div>
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            required
                            onChange={(e) => handleImageChange(e, 'visitor')}
                            className="hidden"
                          />
                        </label>
                        {visitorImagePreview && (
                          <img src={visitorImagePreview} alt="Visitor" className="h-20 w-20 object-cover rounded-md" />
                        )}
                      </div>
                    </div>

                    {!isWalkIn && (
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700">Vehicle Image *</label>
                        <div className="mt-1 flex items-center space-x-4">
                          <label className="flex-1 cursor-pointer">
                            <div className="flex items-center justify-center px-6 py-4 border-2 border-gray-300 border-dashed rounded-md hover:border-primary-500">
                              <div className="text-center">
                                <Upload className="mx-auto h-8 w-8 text-gray-400" />
                                <p className="mt-1 text-sm text-gray-600">
                                  {formData.vehicleImage ? formData.vehicleImage.name : 'Click to upload vehicle photo'}
                                </p>
                              </div>
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              required={!isWalkIn}
                              onChange={(e) => handleImageChange(e, 'vehicle')}
                              className="hidden"
                            />
                          </label>
                          {vehicleImagePreview && (
                            <img src={vehicleImagePreview} alt="Vehicle" className="h-20 w-20 object-cover rounded-md" />
                          )}
                        </div>
                      </div>
                    )}

                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Notes (Optional)</label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows="2"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={!otpVerified}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Check In
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCheckInModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* OTP Verification Modal */}
      {showOTPModal && (
        <div className="fixed z-20 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="text-center">
                  <Phone className="mx-auto h-12 w-12 text-primary-600" />
                  <h3 className="mt-2 text-lg font-medium text-gray-900">Verify Visitor Phone</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Enter the 6-digit OTP sent to {formData.visitorPhone}
                  </p>
                </div>

                <div className="mt-4">
                  <input
                    type="text"
                    maxLength="6"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="Enter OTP"
                    className="block w-full text-center text-2xl tracking-widest border border-gray-300 rounded-md shadow-sm py-3 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="mt-4 flex justify-between text-sm">
                  <button
                    type="button"
                    onClick={handleSendOTP}
                    className="text-primary-600 hover:text-primary-700"
                  >
                    Resend OTP
                  </button>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleVerifyOTP}
                  disabled={otp.length !== 6}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Verify
                </button>
                <button
                  type="button"
                  onClick={() => setShowOTPModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vehicles;
